"use client";

import Image from "next/image";
import {useImageSizes} from "@core/helpers/client/contexts/ImageSizesContext";
import {useCallback, useMemo, useState} from "react";
import {useWebSiteContext} from "@core/helpers/client/contexts/WebSiteContext";
import {
	image_priority_props,
	resolve_image_priority,
	shows_skeleton,
	useImageHidden,
	useImagePriority,
} from "@core/helpers/client/contexts/ImagePriorityContext";

const NoImage = ({
	                 width,
	                 height,
	                 style,
	                 className,
	                 options,
	                 custom_default_svg
                 }) => {

	const more_styles = useMemo(() => {
		let _styles = {...style};
		if (_styles?.width) {
			delete _styles.width
		}
		if (_styles?.height) {
			delete _styles.height
		}
		return _styles;
	}, [style])

	const calc_height = useMemo(() => {
		if (style?.height) {
			if (style?.height !== 'auto' || style?.height !== 'auto !important') {
				return style?.height
			}
		}
		return `${height}px !important`
	}, [style, height]);

	return (
		<div
			className={`no-image ${className || ''}`}
			style={{
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				width: style?.width || `${width}px`,
				height: calc_height,
				...more_styles
			}}
		>
			{custom_default_svg ?
				custom_default_svg :
				<svg
					fill={options.default_svg_fill || "#5a5a5a"}
					stroke={options.default_svg_stroke || 'none'}
					width={options.default_svg_width || "50px"}
					height={options.default_svg_height || "50px"}
					viewBox="0 0 32 32"
					xmlns="http://www.w3.org/2000/svg"
				>
					<path
						d="M30,3.4141,28.5859,2,2,28.5859,3.4141,30l2-2H26a2.0027,2.0027,0,0,0,2-2V5.4141ZM26,26H7.4141l7.7929-7.793,2.3788,2.3787a2,2,0,0,0,2.8284,0L22,19l4,3.9973Zm0-5.8318-2.5858-2.5859a2,2,0,0,0-2.8284,0L19,19.1682l-2.377-2.3771L26,7.4141Z"/>
					<path
						d="M6,22V19l5-4.9966,1.3733,1.3733,1.4159-1.416-1.375-1.375a2,2,0,0,0-2.8284,0L6,16.1716V6H22V4H6A2.002,2.002,0,0,0,4,6V22Z"/>
					<rect
						id="_Transparent_Rectangle_"
						data-name="&lt;Transparent Rectangle&gt;"
						width="32"
						fill='none'
						height="32"
					/>
				</svg>
			}
		</div>
	)
}


const myLoader = ({src, width, quality}) => {
	return `${src}?w=${width}&q=${quality || 75}`
}

// Same as image.js: the CDN ignores `w` and `q`, so images go through Next's optimizer (resized AVIF/WebP). The URL is
// built here so development never throws for a host missing from `images.remotePatterns`; a refused host answers 400
// and `onError` retries the same image with `myLoader` (the old direct URL) before showing the fallback.
// Path from the site's build config, as in image.js ("/_next/image/" when `trailingSlash` is on; no 308).
const image_opts = process.env.__NEXT_IMAGE_OPTS;
const optimizer_path = image_opts?.path || "/_next/image";
const optimizer_loader = ({src, width, quality}) =>
	`${optimizer_path}?url=${encodeURIComponent(src)}&w=${width}&q=${quality || 75}`;

// Only the site's own images are optimized: files under its storage prefix and local `/...` paths. Third-party images
// (trust seals such as eNamad check the visitor's referrer, other hosts may not be in `remotePatterns`) stay direct from
// the browser, exactly as before. SVG (refused by the optimizer), GIF (animation is kept as is) and data/blob URLs too.
const is_direct_only = (src, storage_prefix) => {
	if (!src || /\.(svg|gif)(\?|#|$)/i.test(src) || /^(data|blob):/i.test(src)) return true;
	const is_local = src.startsWith("/") && !src.startsWith("//");
	const is_storage = !!storage_prefix && src.startsWith(storage_prefix);
	return !(is_local || is_storage);
};

// Without `sizes` next/image only offers 1x/2x of the `width` prop, and callers often pass a small width (100) while CSS
// shows the image larger: on a 3x phone a 154px card got at most a 256px file, softer than the original it used to get.
// With the same responsive default as image.js the browser can always pick a large enough file; the optimizer never
// upscales past the original, so quality is never below the old direct file. Pass `sizes` to get a smaller file.
const DEFAULT_SIZES = "(max-width:768px)100vw,886px";

const ImageComponent = ({
	                        media,
	                        src,
	                        placeholder_src,
	                        style,
	                        width,
	                        height,
	                        placeholder_width,
	                        placeholder_height,
	                        default_src_options = {},
	                        custom_default_svg,
	                        className,
	                        loading_image_src,
	                        alt,
	                        // true → "lcp", false → "lazy"; not passed → ImagePriorityProvider, then "lazy" (the old main image)
	                        isLCP,
	                        priority_level,
	                        // plain next/image priority props still work: they are folded into the level
	                        priority,
	                        preload,
	                        loading,
	                        fetchPriority,
	                        // taken out of `props` so an explicit undefined does not wipe the slider / default value
	                        sizes,
	                        // the prefix for a relative `src`; the site's own prefix when not given (it used to leak onto <img>)
	                        storage_prefix_url,
	                        ...props
                        }) => {

	const {web_site_setting: {web_site_storage_prefix_url: site_prefix}} = useWebSiteContext();
	const web_site_storage_prefix_url = storage_prefix_url || site_prefix;
	const level = resolve_image_priority({
		hidden: useImageHidden(),
		priority_level,
		next_props: {priority, preload, loading, fetchPriority},
		isLCP,
		context: useImagePriority(),
		fallback: "lazy",
	});
	// a slider or grid around this image may know its column width (ImageSizesProvider); an explicit `sizes` prop wins
	const context_sizes = useImageSizes();
	const image_width = width || media?.media?.information?.width || placeholder_width;
	const image_height = height || media?.media?.information?.height || placeholder_height;

	const full_src = useMemo(() => {
		const _src = src || media?.url || placeholder_src;
		if (_src?.startsWith('http') || _src?.startsWith('/')) {
			return _src
		}
		return `${web_site_storage_prefix_url}/${_src}`
	}, [src, media, placeholder_src])

	const [error, set_error] = useState(false);
	const [complete_load_img, set_complete_load_img] = useState(false);
	// the source whose optimized URL failed; that source is then loaded directly from the CDN as before
	const [direct_src, set_direct_src] = useState(null);
	const use_direct = direct_src === full_src || is_direct_only(full_src, web_site_storage_prefix_url);

	// an image the browser already finished before hydration fires no onLoad that React sees
	const mark_if_complete = useCallback((node) => {
		if (node?.complete && node.naturalWidth > 0) set_complete_load_img(true);
	}, []);

	const handle_error = () => {
		// first failure of an optimized URL: try the same image directly before showing the fallback
		if (!use_direct) {
			set_direct_src(full_src)
			return
		}
		set_error(true)
		set_complete_load_img(true)
	};

	// The image is in the server HTML at its final size from the first paint. It used to be rendered `width:0;
	// height:0` with a skeleton in its place until onLoad ran after hydration, so product images, covers and sliders
	// appeared only seconds after the HTML (and LCP waited for JS). Lazy images show the skeleton as their own
	// background until they load; images in the first viewport show none.
	// nothing to load (no src, no media, no placeholder): the fallback right away instead of two failing requests
	if (!src && !media?.url && !placeholder_src) {
		return (
			<NoImage
				style={style}
				className={className}
				options={default_src_options}
				custom_default_svg={custom_default_svg}
				width={image_width}
				height={image_height}
			/>
		);
	}

	if (!error && !loading_image_src) {
		const skeleton = shows_skeleton(level) && !complete_load_img ? " shimmer-animation" : "";
		return (
			<Image
				ref={mark_if_complete}
				loader={use_direct ? myLoader : optimizer_loader}
				className={`${className || ""}${skeleton}`}
				src={full_src}
				overrideSrc={full_src}
				sizes={sizes || context_sizes || DEFAULT_SIZES}
				alt={alt || ""}
				placeholder={'empty'}
				width={image_width}
				height={image_height}
				style={props?.fill ? {...style} : {
					maxWidth: "100%",
					width: style?.width ? style?.width : `${image_width}px`,
					height: style?.height ? style?.height : `${image_height}px`,
					...style
				}}
				{...image_priority_props(level)}
				onError={handle_error}
				onLoad={() => {
					set_complete_load_img(true)
				}}
				{...((alt || media?.alt) ? {alt: alt || media?.alt} : {})}
				{...props}
			/>
		);
	}

	// `loading_image_src` (no site uses it today): the old flow, a placeholder image until the real one loads
	return (
		error ?
			placeholder_src ?
				<Image
					src={placeholder_src}
					{...({
						style,
						width: placeholder_width || width || 100,
						height: placeholder_height || height || 100,
						className,
						alt: "placeholder",
						...props
					})}
				/>
				:
				<NoImage
					{...({
						style,
						className,
						options: default_src_options,
						custom_default_svg,
						width: width || media?.media?.information?.width,
						height: height || media?.media?.information?.height,
						...props,
					})}
				/>
			:
			(
				<>
					{complete_load_img ?
						<></>
						:
						(loading_image_src ?
								<Image
									className={className || ''}
									loader={myLoader}
									src={loading_image_src}
									alt="loading_placeholder"
									width={width || media?.media?.information?.width || placeholder_width}
									height={height || media?.media?.information?.height || placeholder_height}
									style={{
										objectFit: 'cover',
										...style
									}}
									fetchPriority={isLCP ? "high" : "auto"}
									loading={isLCP ? "eager" : "lazy"}
								/>
								:
								<div
									className={className}
									style={{
										width: style?.width || "100%",
										maxWidth: '100%',
										aspectRatio: `${width || media?.media?.information?.width} / ${height || media?.media?.information?.height}`,
										overflow: "hidden",
										...style,
									}}>
									<div
										className="shimmer-animation"
										style={{
											width: `100%`,
											height: `100%`,
										}}
									/>
								</div>
						)
					}
					<Image
						loader={use_direct ? myLoader : optimizer_loader}
						className={className}
						src={full_src}
						overrideSrc={full_src}
						sizes={sizes || context_sizes || DEFAULT_SIZES}
						alt={alt || ""}
						qualities={[100, 75]}
						placeholder={'empty'}
						width={width || media?.media?.information?.width || placeholder_width}
						height={height || media?.media?.information?.height || placeholder_height}
						style={{
							...(complete_load_img ?
								props?.fill ? {
										...style
									} :
									{
										maxWidth: "100%",
										width: style?.width ? style?.width : `${width || media?.media?.information?.width}px`,
										height: style?.height ? style?.height : `${height || media?.media?.information?.height}px`,
										...style
									} : {
									width: 0,
									height: 0,
								}),
						}}
						onError={(e) => {
							// first failure of an optimized URL: try the same image directly before showing the fallback
							if (!use_direct) {
								set_direct_src(full_src)
								return
							}
							set_error(true)
							set_complete_load_img(true)
						}}
						onLoad={() => {
							set_complete_load_img(true)
						}}
						{...image_priority_props(level)}
						{...((alt || media?.alt) ? {alt: alt || media?.alt} : {})}
						{...props}
					/>

				</>
			)
	);
};

export default ImageComponent;